import { useState, useEffect, useRef } from "react";
import { useCreateRenderMutation } from "@/api/renders";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { RenderProgress } from "./RenderProgress";
import { RenderControls } from "./RenderControls";
import {
  DEFAULT_RENDER_SETTINGS,
  type OutputFormat,
  type RenderSettings,
} from "@/types/render";

interface RenderButtonProps {
  templateId: string;
  inputProps: Record<string, unknown>;
  defaultFormat?: OutputFormat;
  /** Optional initial settings overrides — typically the template's native size/fps. */
  initialSettings?: Partial<RenderSettings>;
  /**
   * When true, the ⚙ "MP4 · 1920×1080 · 30fps" pill is suppressed and only
   * the Render button is rendered. Used by TemplateDetail where the render
   * settings live in the dedicated "Render" tab in the left tab strip —
   * surfacing them twice would be noise.
   */
  hideSettingsPill?: boolean;
}

export function RenderButton({
  templateId,
  inputProps,
  defaultFormat = "mp4",
  initialSettings,
  hideSettingsPill = false,
}: RenderButtonProps) {
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [storedSettings, setSettings] = useLocalStorage<RenderSettings>(
    "motionforge-render-settings",
    {
      ...DEFAULT_RENDER_SETTINGS,
      format: defaultFormat,
      ...initialSettings,
    },
  );

  // Duration in Auto mode (the default) always follows the live value the
  // parent derives from the template's timing fields — the stored blob is
  // both global across templates and frozen at whatever was last saved, so
  // trusting it is exactly how a 5-minute countdown renders 5 seconds.
  const derivedDuration = initialSettings?.duration_frames;
  const settings: RenderSettings =
    storedSettings.duration_auto !== false && derivedDuration && derivedDuration > 0
      ? { ...storedSettings, duration_frames: derivedDuration }
      : storedSettings;
  const [showControls, setShowControls] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createRender = useCreateRenderMutation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close controls on outside click
  useEffect(() => {
    if (!showControls) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowControls(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setShowControls(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showControls]);

  function handleRender(e?: React.MouseEvent) {
    e?.preventDefault();
    e?.stopPropagation();
    setError(null);
    createRender.mutate(
      {
        template_id: templateId,
        props: inputProps,
        format: settings.format,
        prores_profile: settings.prores_profile,
        width: settings.width,
        height: settings.height,
        fps: settings.fps,
        duration_frames: settings.duration_frames,
        crf: settings.crf,
        content_scale: settings.content_scale,
        // Only for alpha-capable ProRes profiles, so other formats' dedup
        // hashes are untouched by the new field.
        dither:
          settings.format === "prores" &&
          (settings.prores_profile === "4444" ||
            settings.prores_profile === "4444-xq")
            ? settings.dither !== false
            : undefined,
      },
      {
        onSuccess: (job: { id: string }) => {
          setActiveJobId(job.id);
          setShowControls(false);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : "Render failed");
        },
      },
    );
  }

  return (
    <div ref={containerRef} className="relative flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {!hideSettingsPill && (
          <button
            type="button"
            onClick={() => setShowControls((v) => !v)}
            className="rounded-lg border border-line bg-surface px-2 py-1.5 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
            title="Render settings"
            aria-label="Toggle render settings"
          >
            ⚙ {settings.format.toUpperCase()} · {settings.width}×{settings.height} · {settings.fps}fps
          </button>
        )}
        <button
          type="button"
          onClick={handleRender}
          disabled={createRender.isPending}
          className="rounded-lg bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          {createRender.isPending ? "Starting..." : "Render"}
        </button>
      </div>

      {showControls && (
        <div className="absolute right-0 top-full z-50 mt-2">
          <RenderControls
            settings={settings}
            onChange={setSettings}
            onClose={() => setShowControls(false)}
          />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-1.5 text-xs text-danger">
          {error}
        </div>
      )}
      {activeJobId && (
        <RenderProgress
          jobId={activeJobId}
          onDismiss={() => setActiveJobId(null)}
        />
      )}
    </div>
  );
}
